import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useConfiguration } from '../../../context/configurationContext';
import { fetchRecommendedListings } from '../ListingPage.duck';
import { isCarPayload } from '../../../util/savedCar';
import { fitBandOf } from '../../SearchPage/resultCardData';
import { ListingCard } from '../../../components';

import css from './SectionRecommendedListings.module.css';

/**
 * The row under a listing: only listings that fit the one on screen.
 *
 * P25009-482 BR-11 — the heading names what the buyer is shopping for and for
 * which car: "Andra vinterhjul till din V90". The model is the buyer's own car
 * when the page knows it, otherwise the car this listing is for. Every card
 * shows the price for a full set and, with a car known, its own fit badge
 * (ListingCard). The seller's-other-listings row ("Fler annonser från …") is
 * gone: it competed with the one listing the buyer came to decide on.
 *
 * Which listings may appear is decided in util/recommendedListings.js.
 */
const WINTER_TYPES = ['winterFriction', 'winterDubbed'];

/* "vinterhjul" / "sommardäck" / "fälgar" — the listing's kind, with its
 * season when it has one. R- and C-däck are not seasons. */
export const productPhrase = (publicData = {}) => {
  const type = publicData.adType || publicData.listingType || '';
  if (['rim', 'businesswheel'].includes(type)) return 'fälgar';
  const noun = ['tire', 'businesstire'].includes(type) ? 'däck' : 'hjul';
  const season = String(publicData.sasong || '').toLowerCase();
  if (WINTER_TYPES.includes(publicData.tiretype) || /vinter|winter/.test(season)) {
    return `vinter${noun}`;
  }
  if (publicData.tiretype === 'summer' || /sommar|summer/.test(season)) return `sommar${noun}`;
  if (/helår|allseason|all_season/.test(season)) return `helårs${noun}`;
  return noun;
};

export const fitRowHeading = (publicData, carModel) => {
  const phrase = productPhrase(publicData);
  return carModel ? `Andra ${phrase} till din ${carModel}` : `Andra ${phrase} som passar`;
};

const ArrowSvg = ({ dir }) => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d={dir === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
    />
  </svg>
);

const ListingRow = ({ heading, listings }) => {
  const scrollRef = useRef(null);

  const scroll = direction => {
    if (!scrollRef.current) return;
    const card = scrollRef.current.querySelector('[data-card]');
    const step = (card?.offsetWidth || 280) + 16;
    scrollRef.current.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  // No heading, no skeleton, no empty state when the row has nothing to
  // show. A short row is fine; a padded one is not (BR-6).
  if (!listings.length) return null;

  return (
    <section className={css.section} aria-label={heading}>
      <div className={css.sectionHead}>
        <h2 className={css.heading}>{heading}</h2>
        <div className={css.scrollBtns}>
          <button
            type="button"
            onClick={() => scroll(-1)}
            className={css.scrollBtn}
            aria-label="Föregående"
          >
            <ArrowSvg dir="left" />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            className={css.scrollBtn}
            aria-label="Nästa"
          >
            <ArrowSvg dir="right" />
          </button>
        </div>
      </div>

      <div ref={scrollRef} className={css.listingsScroller}>
        {listings.map(l => (
          <div key={l.id.uuid} data-card className={css.listingCardCompact}>
            <ListingCard listing={l} showFavoriteIcons />
          </div>
        ))}
      </div>
    </section>
  );
};

const SectionRecommendedListings = props => {
  const { listing, carModel } = props;
  const config = useConfiguration();
  const dispatch = useDispatch();

  // Local state instead of Redux entities: these listings are denormalised
  // by the thunk and held here so navigating to a clicked card doesn't tear
  // down DOM mid-route-change (removeChild bug) and doesn't overwrite the
  // current listing's image variants in the shared entity store (gallery
  // blank bug).
  //
  // Seeded from the data loaded in loadData (ListingPage duck slice, NOT the
  // shared entity store) so both rows are part of the server-rendered,
  // crawlable HTML. The effect below keeps them fresh on in-app navigation
  // between listings.
  const storedFor = useSelector(state => state.ListingPage.recommendedForListingId);
  const buyerCar = useSelector(state => state.currentLicensePlate?.currentLicensePlate || null);
  const storedFit = useSelector(state => state.ListingPage.recommendedListings);
  const storedSeller = useSelector(state => state.ListingPage.sellerListings);

  const [rows, setRows] = useState({
    listingId: storedFor,
    fit: storedFit || [],
    seller: storedSeller || [],
  });

  const listingId = listing?.id?.uuid;

  useEffect(() => {
    if (!listingId) return undefined;
    // loadData already fetched both rows for this listing, so a normal page
    // load asks the API once rather than twice.
    if (storedFor === listingId) {
      setRows({ listingId, fit: storedFit || [], seller: storedSeller || [] });
      return undefined;
    }
    let cancelled = false;
    dispatch(fetchRecommendedListings(listing, config)).then(result => {
      if (cancelled) return;
      setRows({
        listingId,
        fit: result?.listings || [],
        seller: result?.sellerListings || [],
      });
    });
    return () => {
      cancelled = true;
    };
    // `listing` is intentionally omitted from deps — it's a denormalised
    // SDK entity that gets a fresh object reference on every parent
    // re-render even when its id is unchanged, so adding it would re-fetch
    // on every ListingPage render. The two stored arrays are omitted for
    // the same reason; `storedFor` is a plain id and is enough to tell
    // whether they belong to the listing on screen.
    //
    // No eslint-disable directive here on purpose: this project's eslint setup
    // does not register the react-hooks plugin, so naming that rule in a
    // directive is itself an error ("Definition for rule ... was not found")
    // and CRA raises it as a full-screen dev overlay.
  }, [listingId, storedFor, dispatch, config]);

  // While a new listing's rows are still loading, show nothing rather than
  // the previous listing's — those would not fit this car.
  if (!listingId || rows.listingId !== listingId) return null;

  /* P25009-486 BR-6 — with the buyer's car known, only what the fit check
   * says fits it ("✓ Passar din …"); the row is about that car. */
  const car = isCarPayload(buyerCar) ? buyerCar : null;
  const fitRows = car ? rows.fit.filter(l => fitBandOf(l, car) === 0) : rows.fit;

  return (
    <ListingRow
      heading={fitRowHeading(listing?.attributes?.publicData, carModel)}
      listings={fitRows}
    />
  );
};

export default SectionRecommendedListings;
